import {TimePicker} from "../../../../../../components/fields/time/TimePicker";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {UiComponent} from "../model/UiComponent";
import {t} from "../../../../../../store/localization/TranslMessasge";
import {TimePickerField} from "../model/TimePickerField";
import {ValuePersister} from "./ValuePersister";
import {PropertyValuePersister} from "./PropertyValuePersister";
import elementService from "../../../elementdetail/service/ElementService";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";

export class TimePickerFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["TimePickerField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValueAsDate(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValueAsDate(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): Date | undefined {
        const value = this.getPropertyValue(element, attributeDefinition);
        return value ? new Date(value) : undefined;
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string | undefined {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return undefined;
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const timePickerField = component as TimePickerField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[timePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        const value = stateContext.values[timePickerField.attributeDefinitionId];
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const timePickerField = component as TimePickerField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[timePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={timePickerField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[timePickerField.attributeDefinitionId];
        const error = stateContext.errors[timePickerField.attributeDefinitionId];
        const readOnly = timePickerField.readOnly || !stateContext.editMode;

        return (
                <TimePicker
                        key={timePickerField.id}
                        label={attributeDefinition.name}
                        value={value ?? ""}
                        onChange={(value) => stateContext.onValuesChange({[timePickerField.attributeDefinitionId]: value})}
                        readOnly={readOnly}
                        required={attributeDefinition.required}
                        error={error}
                        helperText={timePickerField.helperText}/>
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const timePickerField = component as TimePickerField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[timePickerField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[timePickerField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}
